import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3001' : 'https://api.makealign.com');

const MakeAlign = () => {
  const [view, setView] = useState('upload'); // upload, auth, analyzing, results
  const [file, setFile] = useState(null);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [authMode, setAuthMode] = useState('login'); // login, signup
  
  // Auth form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');

  // Handle OAuth callback
useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  
  if (token) {
    localStorage.setItem('makealign_token', token);
    // Clean URL
    window.history.replaceState({}, '', '/');
    // Fetch user data
    fetchUser(token);
  }
}, []);
  
  // Contact form state
  const [fundraising, setFundraising] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [challenge, setChallenge] = useState('');
  const [requestPromo, setRequestPromo] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Promo code state
  const [showPromoInput, setShowPromoInput] = useState(false);
  const [promoCode, setPromoCode] = useState('');

  // Check for existing token on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('makealign_token');
    if (savedToken) {
      setToken(savedToken);
      fetchUser(savedToken);
    }
  }, []);

  const fetchUser = async (authToken) => {
    try {
      const response = await fetch(`${API_URL}/auth/me`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        localStorage.removeItem('makealign_token');
        setToken(null);
      }
    } catch (error) {
      console.error('Fetch user error:', error);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      setError('');
    } else {
      setError('PDF files only');
    }
  };

  const handleAnalyzeClick = () => {
    if (!file) {
      setError('Please select a file');
      return;
    }
    if (!token) {
      setView('auth');
    } else {
      startAnalysis();
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      if (response.ok) {
        const data = await response.json();
        setToken(data.token);
        localStorage.setItem('makealign_token', data.token);
        setUser(data.user);
        startAnalysis(data.token);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Login failed');
      }
    } catch (error) {
      setError('Login failed');
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name })
      });

      if (response.ok) {
        const data = await response.json();
        setToken(data.token);
        localStorage.setItem('makealign_token', data.token);
        setUser(data.user);
        startAnalysis(data.token);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Signup failed');
      }
    } catch (error) {
      setError('Signup failed');
    }
  };

  const startAnalysis = async (authToken = token) => {
    setView('analyzing');
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API_URL}/api/analyze`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setResults(data);
        setView('results');
      } else if (response.status === 402) {
        const errorData = await response.json();
        setError(errorData.message);
        setView('upload');
        setShowPromoInput(true);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Analysis failed');
        setView('upload');
      }
    } catch (error) {
      setError('Analysis failed');
      setView('upload');
    }
  };

  const handleApplyPromo = async () => {
    try {
      const response = await fetch(`${API_URL}/payments/apply-promo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code: promoCode })
      });

      if (response.ok) {
        const data = await response.json();
        alert(data.message);
        setPromoCode('');
        setShowPromoInput(false);
        await fetchUser(token);
      } else {
        const errorData = await response.json();
        alert(errorData.error);
      }
    } catch (error) {
      alert('Failed to apply promo code');
    }
  };

  const handleContactSubmit = async (e) => {
  e.preventDefault();
  try {
    const response = await fetch(`${API_URL}/api/contact`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        fundraising,
        email: contactEmail,
        challenge,
        requestPromo,
        fileName: file?.name
      })
    });

    if (response.ok) {
      setSubmitted(true);
    } else {
      alert('Failed to submit. Please try again.');
    }
  } catch (error) {
    alert('Failed to submit. Please try again.');
  }
};

  const handleLogout = () => {
    localStorage.removeItem('makealign_token');
    setToken(null);
    setUser(null);
    setView('upload');
    setFile(null);
    setResults(null);
  };

  const handleReset = () => {
    setView('upload');
    setFile(null);
    setResults(null);
    setFundraising('');
    setContactEmail('');
    setChallenge('');
    setRequestPromo(false);
    setSubmitted(false);
  };

  // Analyzing view
if (view === 'analyzing') {
  return (
    <>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }
      `}</style>
      
      <div style={{
        minHeight: '100vh',
        background: '#FFFFFF',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 24px',
        fontFamily: 'Inter, -apple-system, sans-serif'
      }}>
        <div style={{ maxWidth: '640px', width: '100%' }}>
          <div style={{
            fontSize: '32px',
            fontWeight: '700',
            color: '#000000',
            marginBottom: '32px',
            letterSpacing: '-0.02em'
          }}>
            ANALYZING
          </div>
          <div style={{
            fontSize: '14px',
            color: '#6B7280',
            lineHeight: '1.8'
          }}>
            <div style={{ animation: 'pulse 1.5s ease-in-out infinite' }}>→ VERIFYING CLAIMS</div>
            <div style={{ animation: 'pulse 1.5s ease-in-out infinite 0.3s' }}>→ CHECKING DECK COVERAGE</div>
            <div style={{ animation: 'pulse 1.5s ease-in-out infinite 0.6s' }}>→ CATCHING CONTRADICTIONS</div>
            <div style={{ animation: 'pulse 1.5s ease-in-out infinite 0.9s' }}>→ FLAGGING VAGUE PHRASES</div>
            <div style={{ animation: 'pulse 1.5s ease-in-out infinite 1.2s' }}>→ CALCULATING DILIGENCE READINESS</div>
          </div>
          <div style={{
            marginTop: '48px',
            fontSize: '12px',
            color: '#9CA3AF'
          }}>
            Thorough analysis typically takes 1-2 minutes...
          </div>
        </div>
      </div>
    </>
  );
}

  // Auth view
  if (view === 'auth') {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#FFFFFF',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 24px',
        fontFamily: 'Inter, -apple-system, sans-serif'
      }}>
        <div style={{ maxWidth: '480px', width: '100%' }}>
        <div style={{
            fontSize: '14px',
            fontWeight: '700',
            color: '#000000',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            marginBottom: '48px'
          }}>
            <>MAKEALIGN <span style={{ 
              color: '#9CA3AF', 
              fontWeight: '400',
              letterSpacing: '0.05em'
            }}>DECK PRE-DILIGENCE</span></>
          </div>

          <div style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#000000',
            marginBottom: '32px',
            letterSpacing: '-0.02em'
          }}>
            Sign in to analyze
          </div>

          <div style={{
            textAlign: 'center',
            fontSize: '12px',
            color: '#9CA3AF',
            marginBottom: '24px'
          }}>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={authMode === 'login' ? handleLogin : handleSignup}>
            {authMode === 'signup' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '11px',
                  color: '#6B7280',
                  textTransform: 'uppercase',
                  fontWeight: '600',
                  letterSpacing: '0.05em',
                  marginBottom: '8px'
                }}>
                  NAME
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #E5E7EB',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                />
              </div>
            )}

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '11px',
                color: '#6B7280',
                textTransform: 'uppercase',
                fontWeight: '600',
                letterSpacing: '0.05em',
                marginBottom: '8px'
              }}>
                EMAIL
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #E5E7EB',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '11px',
                color: '#6B7280',
                textTransform: 'uppercase',
                fontWeight: '600',
                letterSpacing: '0.05em',
                marginBottom: '8px'
              }}>
                PASSWORD
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #E5E7EB',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>

            {error && (
              <div style={{
                padding: '12px',
                background: '#FEF2F2',
                border: '1px solid #FCA5A5',
                color: '#991B1B',
                fontSize: '13px',
                marginBottom: '16px'
              }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              style={{
                width: '100%',
                padding: '16px',
                background: '#000000',
                border: 'none',
                color: '#FFFFFF',
                fontSize: '14px',
                fontWeight: '700',
                cursor: 'pointer',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: '16px'
              }}
            >
              {authMode === 'login' ? 'SIGN IN' : 'CREATE ACCOUNT'}
            </button>
          </form>

          <div style={{
            textAlign: 'center',
            fontSize: '13px',
            color: '#6B7280'
          }}>
            {authMode === 'login' ? (
              <>
                Don't have an account?{' '}
                <button
                  onClick={() => setAuthMode('signup')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#000000',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontWeight: '600'
                  }}
                >
                  Sign up
                </button>
              </>
            ) : (
              <>
                Already have an account?{' '}
                <button
                  onClick={() => setAuthMode('login')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#000000',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontWeight: '600'
                  }}
                >
                  Sign in
                </button>
              </>
            )}
          </div>

          {/* Forgot password */}
          <div style={{
            textAlign: 'center',
            fontSize: '12px',
            color: '#6B7280',
            marginTop: '16px'
          }}>
            Forgot password?{' '}
            <a
              href="mailto:sanchit.waray@gmail.com?subject=MakeAlign Password Reset"
              style={{
                color: '#000000',
                textDecoration: 'underline',
                fontWeight: '600'
              }}
            >
              Email us
            </a>
          </div>

          <button
            onClick={() => setView('upload')}
            style={{
              marginTop: '32px',
              background: 'none',
              border: 'none',
              color: '#6B7280',
              fontSize: '12px',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
          >
            ← Back to upload
          </button>
        </div>
      </div>
    );
  }

  // Results view (will be imported from MakeAlign-Final.jsx logic)
  if (view === 'results' && results) {
    return (
      <ResultsView
        results={results}
        fileName={file?.name}
        onReset={handleReset}
        fundraising={fundraising}
        setFundraising={setFundraising}
        contactEmail={contactEmail}
        setContactEmail={setContactEmail}
        challenge={challenge}
        setChallenge={setChallenge}
        requestPromo={requestPromo}
        setRequestPromo={setRequestPromo}
        handleContactSubmit={handleContactSubmit}
        submitted={submitted}
        user={user}
      />
    );
  }

  // Upload view (default)
  return (
    <div style={{
      minHeight: '100vh',
      background: '#FFFFFF',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '48px 24px',
      fontFamily: 'Inter, -apple-system, sans-serif'
    }}>
      <div style={{ maxWidth: '640px', width: '100%' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px'
        }}>
         <div style={{
            fontSize: '14px',
            fontWeight: '700',
            color: '#000000',
            textTransform: 'uppercase',
            letterSpacing: '0.1em'
          }}>
            <>MAKEALIGN <span style={{ 
              color: '#9CA3AF', 
              fontWeight: '400',
              letterSpacing: '0.05em'
            }}>DECK PRE-DILIGENCE</span></>
          </div>
          {user && (
            <button
              onClick={handleLogout}
              style={{
                background: 'none',
                border: 'none',
                color: '#6B7280',
                fontSize: '12px',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
            >
              Sign out
            </button>
          )}
        </div>

        {/* Tagline */}
        <div style={{
          fontSize: '16px',
          color: '#6B7280',
          marginBottom: '48px',
          lineHeight: '1.5'
        }}>
          Check the numbers. Catch the conflicts. Prep for diligence.
        </div>

        {/* Upload Box */}
        <div
          style={{
            border: '2px solid #000000',
            padding: '64px 32px',
            textAlign: 'center',
            marginBottom: '24px',
            cursor: 'pointer',
            background: file ? '#F9FAFB' : '#FFFFFF'
          }}
          onClick={() => document.getElementById('file-input').click()}
        >
          <input
            type="file"
            accept=".pdf"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            id="file-input"
          />

          <div style={{ fontSize: '48px', marginBottom: '24px' }}>↑</div>

          <div style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#000000',
            marginBottom: '8px',
            letterSpacing: '-0.01em'
          }}>
            {file ? file.name : 'UPLOAD PITCH DECK'}
          </div>

          <div style={{
            fontSize: '12px',
            color: '#6B7280',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            fontWeight: '600'
          }}>
            PDF ONLY • MAX 10MB
          </div>
        </div>

        {/* Privacy message */}
        <div style={{
          fontSize: '11px',
          color: '#6B7280',
          textAlign: 'center',
          marginBottom: '24px',
          fontStyle: 'italic'
        }}>
          🔒 Your deck is not stored. Encrypted in transit and never shared
        </div>

        {/* Usage Info */}
        {user && (
          <div style={{
            padding: '16px',
            background: '#F9FAFB',
            border: '1px solid #E5E7EB',
            fontSize: '13px',
            color: '#374151',
            marginBottom: '24px'
          }}>
            <div style={{ fontWeight: '600', marginBottom: '4px' }}>
              Analyses remaining: {(user.usage.freeAnalysesRemaining || 0) + (user.usage.promoAnalysesRemaining || 0)}
            </div>
            <div style={{ fontSize: '12px', color: '#6B7280' }}>
              {user.usage.freeAnalysesRemaining > 0 && `${user.usage.freeAnalysesRemaining} free`}
              {user.usage.promoAnalysesRemaining > 0 && ` + ${user.usage.promoAnalysesRemaining} promo`}
            </div>
          </div>
        )}

        {/* Promo Code Input */}
        {showPromoInput && user && (
          <div style={{
            padding: '20px',
            background: '#FFFBEB',
            border: '1px solid #FCD34D',
            marginBottom: '24px'
          }}>
            <div style={{
              fontSize: '12px',
              fontWeight: '600',
              color: '#92400E',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '12px'
            }}>
              ENTER PROMO CODE
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                placeholder=" "
                style={{
                  flex: 1,
                  padding: '12px',
                  border: '1px solid #E5E7EB',
                  fontSize: '14px',
                  textTransform: 'uppercase'
                }}
              />
              <button
                onClick={handleApplyPromo}
                style={{
                  padding: '12px 24px',
                  background: '#000000',
                  border: 'none',
                  color: '#FFFFFF',
                  fontSize: '12px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  textTransform: 'uppercase'
                }}
              >
                APPLY
              </button>
            </div>
            <div style={{
              fontSize: '11px',
              color: '#78350F',
              marginTop: '8px'
            }}>
              Get more analyses with a promo code
            </div>
          </div>
        )}

        {/* Analyze Button */}
        {file && (
          <button
            onClick={handleAnalyzeClick}
            style={{
              width: '100%',
              padding: '20px',
              background: '#000000',
              border: 'none',
              color: '#FFFFFF',
              fontSize: '14px',
              fontWeight: '700',
              cursor: 'pointer',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '24px'
            }}
          >
            {token ? 'ANALYZE DECK' : 'SIGN IN TO ANALYZE'}
          </button>
        )}

        {/* Error */}
        {error && (
          <div style={{
            padding: '16px',
            background: '#FEF2F2',
            border: '1px solid #FCA5A5',
            color: '#991B1B',
            fontSize: '13px',
            marginBottom: '24px'
          }}>
            {error}
          </div>
        )}

        {/* Info */}
        <div style={{
          fontSize: '12px',
          color: '#9CA3AF',
          lineHeight: '1.8',
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
        }}>
          <div>→ VERIFY CLAIMS</div>
<div>→ CHECK DECK COVERAGE</div>
<div>→ CATCH CONTRADICTIONS</div>
<div>→ FLAG VAGUE PHRASES</div>
<div>→ CALCULATE DILIGENCE READINESS</div>
        </div>

        {/* Footer */}
        <div style={{
          marginTop: '64px',
          paddingTop: '24px',
          borderTop: '1px solid #E5E7EB',
          fontSize: '11px',
          color: '#9CA3AF',
          textAlign: 'center',
          letterSpacing: '0.05em'
        }}>
          <div style={{ marginBottom: '8px' }}>
            MAKEALIGN DECK PRE-DILIGENCE v1.1 • BY <a 
              href="https://x.com/pancakepoet" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ 
                color: '#000000', 
                textDecoration: 'none',
                fontWeight: '600'
              }}
            >
              SANCHIT
            </a> • MADE IN CANADA
          </div>
          <div style={{ fontSize: '10px', color: '#D1D5DB' }}>
            NOT INVESTMENT ADVICE
          </div>
        </div>
      </div>
    </div>
  );
};

// Results component (extracted from MakeAlign-Final.jsx)
const ResultsView = ({ results, fileName, onReset, fundraising, setFundraising, contactEmail, setContactEmail, challenge, setChallenge, requestPromo, setRequestPromo, handleContactSubmit, submitted, user }) => {
  return (
    <>
      <style>{`
        @media print {
          button { display: none !important; }
          form { display: none !important; }
        }
      `}</style>
      
      <div style={{
        minHeight: '100vh',
        background: '#FFFFFF',
        padding: '48px 24px',
        fontFamily: 'Inter, -apple-system, sans-serif'
      }}>
        <div style={{
          maxWidth: '960px',
          margin: '0 auto'
        }}>
          {/* Header */}
          <div style={{
            borderBottom: '1px solid #000000',
            paddingBottom: '24px',
            marginBottom: '48px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}>
              <div>
                <div style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  color: '#000000',
                  letterSpacing: '-0.02em',
                  marginBottom: '4px'
                }}>
                  {fileName || 'PITCH DECK ANALYSIS'}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: '#6B7280',
                  textTransform: 'uppercase',
                  fontWeight: '600',
                  letterSpacing: '0.05em'
                }}>
                  {new Date().toISOString().split('T')[0]}
                </div>
              </div>
              <div style={{
                display: 'flex',
                gap: '12px'
              }}>
                <button
                  onClick={() => window.print()}
                  style={{
                    padding: '8px 16px',
                    background: '#FFFFFF',
                    border: '1px solid #000000',
                    color: '#000000',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}
                >
                  PRINT REPORT
                </button>
                <button
                  onClick={onReset}
                  style={{
                    padding: '8px 16px',
                    background: '#FFFFFF',
                    border: '1px solid #000000',
                    color: '#000000',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}
                >
                  NEW ANALYSIS
                </button>
              </div>
            </div>
          </div>

        {/* Readiness Score */}
        <div style={{ marginBottom: '48px' }}>
          <div style={{
            fontSize: '12px',
            color: '#6B7280',
            textTransform: 'uppercase',
            fontWeight: '600',
            letterSpacing: '0.05em',
            marginBottom: '12px'
          }}>
            DILIGENCE READINESS
          </div>
          <div style={{
            fontSize: '96px',
            fontWeight: '700',
            color: '#000000',
            lineHeight: '1',
            letterSpacing: '-0.03em'
          }}>
            {Math.round(((results.criticalMetricsScore || 0) + (results.coverageScore || 0)) / 2)}%
          </div>
          <div style={{
            fontSize: '12px',
            color: '#6B7280',
            marginTop: '12px',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            PROOF READINESS + DECK COVERAGE
          </div>
        </div>

        {/* Stats Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '1px',
          background: '#000000',
          border: '1px solid #000000',
          marginBottom: '64px'
        }}>
          <div style={{
            padding: '24px',
            background: '#FFFFFF',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '36px',
              fontWeight: '700',
              color: '#000000',
              lineHeight: '1',
              marginBottom: '8px'
            }}>
              {results.readyClaims}/{results.totalClaims}
            </div>
            <div style={{
              fontSize: '11px',
              color: '#6B7280',
              textTransform: 'uppercase',
              fontWeight: '600',
              letterSpacing: '0.05em'
            }}>
              CLAIMS VERIFIED
            </div>
          </div>

          <div style={{
  padding: '24px',
  background: '#FFFFFF',
  textAlign: 'center'
}}>
  <div style={{
    fontSize: '36px',
    fontWeight: '700',
    color: '#000000',
    lineHeight: '1',
    marginBottom: '8px'
  }}>
    {results.coverageScore ?? '—'}%
  </div>
  <div style={{
    fontSize: '11px',
    color: '#6B7280',
    textTransform: 'uppercase',
    fontWeight: '600',
    letterSpacing: '0.05em'
  }}>
    DECK COVERAGE
  </div>
</div>
          
          <div style={{
            padding: '24px',
            background: '#FFFFFF',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '36px',
              fontWeight: '700',
              color: '#000000',
              lineHeight: '1',
              marginBottom: '8px'
            }}>
              {results.conflicts}
            </div>
            <div style={{
              fontSize: '11px',
              color: '#6B7280',
              textTransform: 'uppercase',
              fontWeight: '600',
              letterSpacing: '0.05em'
            }}>
              CONFLICTS DETECTED
            </div>
          </div>

          <div style={{
            padding: '24px',
            background: '#FFFFFF',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '36px',
              fontWeight: '700',
              color: '#000000',
              lineHeight: '1',
              marginBottom: '8px'
            }}>
              {results.investorSpeak?.length || 0}
            </div>
            <div style={{
              fontSize: '11px',
              color: '#6B7280',
              textTransform: 'uppercase',
              fontWeight: '600',
              letterSpacing: '0.05em'
            }}>
              VAGUE PHRASES
            </div>
          </div>
</div>
          
        {/* Summary */}
        <div style={{
          padding: '32px',
          background: '#F9FAFB',
          border: '1px solid #E5E7EB',
          marginBottom: '64px'
        }}>
          <div style={{
            fontSize: '14px',
            fontWeight: '700',
            color: '#000000',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '16px'
          }}>
            SUMMARY
          </div>

          <div style={{
            fontSize: '14px',
            color: '#000000',
            fontWeight: '600',
            marginBottom: '12px',
            lineHeight: '1.6'
          }}>
          {(() => {
  const readiness = Math.round(((results.criticalMetricsScore || 0) + (results.coverageScore || 0)) / 2);
  if (readiness < 40 && results.coverageScore >= 70) return 'You are talking about the right things - but most claims need documentation. Get the proof ready for diligence.';
  if (readiness < 40) return 'Most claims need documentation, and your deck is missing topics investors will ask about.';
  if (readiness < 80) return 'Good foundation. A few claims to verify and coverage gaps to close before investor meetings.';
  return 'Your deck is well-documented and diligence-ready.';
})()}
          </div>

          <div style={{
            fontSize: '13px',
            color: '#6B7280',
            marginBottom: '16px',
            paddingBottom: '16px',
            borderBottom: '1px solid #E5E7EB'
          }}>
            Target: 80%+ diligence readiness.
{Math.round(((results.criticalMetricsScore || 0) + (results.coverageScore || 0)) / 2) < 80 && ` ${80 - Math.round(((results.criticalMetricsScore || 0) + (results.coverageScore || 0)) / 2)} more points needed.`}
          </div>

          <div style={{
            fontSize: '14px',
            color: '#374151',
            lineHeight: '1.7'
          }}>
          Your deck was evaluated against {results.totalClaims} diligence checkpoints.
  {results.readyClaims < results.totalClaims && (
    <span> {results.totalClaims - results.readyClaims} need documentation before diligence.</span>
  )}
  {results.coverageScore && results.coverageScore < 100 && (
    <span> {Math.round(results.totalClaims * (100 - results.coverageScore) / 100)} topic{Math.round(results.totalClaims * (100 - results.coverageScore) / 100) === 1 ? ' isn\'t' : 's aren\'t'} addressed at all.</span>
  )}
  {results.conflicts > 0 && (
    <span> {results.conflicts} internal contradiction{results.conflicts > 1 ? 's' : ''} detected across slides.</span>
  )}
  {results.investorSpeak?.length > 0 && (
    <span> {results.investorSpeak.length} phrase{results.investorSpeak.length > 1 ? 's' : ''} need specific metrics.</span>
  )}
  {' '}Fix these before meeting investors.
          </div>
        </div>

        {/* Section 1: Proof Needed */}
        <div style={{ marginBottom: '64px' }}>
          <div style={{
            fontSize: '14px',
            fontWeight: '700',
            color: '#000000',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '8px'
          }}>
            01 — PROOF NEEDED
          </div>
          <div style={{
            fontSize: '13px',
            color: '#6B7280',
            marginBottom: '32px',
            lineHeight: '1.5'
          }}>
            Documents required to verify claims
          </div>

          {results.proofNeeded.map((section, idx) => (
            <div key={idx} style={{ marginBottom: '48px' }}>
              <div style={{
                fontSize: '12px',
                fontWeight: '700',
                color: '#000000',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: '24px',
                paddingBottom: '8px',
                borderBottom: '1px solid #E5E7EB'
              }}>
                {section.name}
              </div>

              {section.claims.map((item, cIdx) => (
                <div key={cIdx} style={{
                  marginBottom: '32px',
                  paddingLeft: '24px',
                  borderLeft: item.status === 'READY' ? '2px solid #10B981' : '2px solid #000000'
                }}>
                  <div style={{ marginBottom: '12px' }}>
                    <span style={{
                      fontSize: '11px',
                      fontWeight: '700',
                      color: item.status === 'READY' ? '#10B981' : '#000000',
                      marginRight: '8px'
                    }}>
                      {item.status === 'READY' ? '✓' : '!'}
                    </span>
                    <span style={{
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#000000'
                    }}>
                      {item.claim}
                    </span>
                    <span style={{
                      fontSize: '11px',
                      color: '#9CA3AF',
                      marginLeft: '8px',
                      textTransform: 'uppercase'
                    }}>
                      {item.location}
                    </span>
                  </div>

                  {item.conflict && (
                    <div style={{
                      padding: '12px',
                      background: '#FEF2F2',
                      border: '1px solid #FCA5A5',
                      marginBottom: '12px',
                      fontSize: '12px',
                      color: '#991B1B',
                      lineHeight: '1.5'
                    }}>
                      <span style={{ fontWeight: '700' }}>CONFLICT: </span>
                      {item.conflict}
                    </div>
                  )}

                  {item.documents && item.documents.length > 0 && (
                    <div style={{ fontSize: '12px', color: '#6B7280', lineHeight: '1.8' }}>
                      {item.documents.map((doc, dIdx) => (
                        <div key={dIdx}>• {doc}</div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Section 2: Investor Speak */}
        {results.investorSpeak && results.investorSpeak.length > 0 && (
          <div style={{ marginBottom: '64px' }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '700',
              color: '#000000',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '8px'
            }}>
              02 — INVESTOR SPEAK
            </div>
            <div style={{
              fontSize: '13px',
              color: '#6B7280',
              marginBottom: '32px',
              lineHeight: '1.5'
            }}>
              Vague language requiring specificity
            </div>

            {results.investorSpeak.map((item, idx) => (
              <div key={idx} style={{
                marginBottom: '24px',
                padding: '20px',
                border: '1px solid #E5E7EB',
                background: '#F9FAFB'
              }}>
                <div style={{
                  fontSize: '11px',
                  color: '#9CA3AF',
                  textTransform: 'uppercase',
                  fontWeight: '600',
                  marginBottom: '16px',
                  letterSpacing: '0.05em'
                }}>
                  {item.location}
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <div style={{
                    fontSize: '11px',
                    color: '#6B7280',
                    textTransform: 'uppercase',
                    fontWeight: '600',
                    marginBottom: '6px',
                    letterSpacing: '0.05em'
                  }}>
                    CURRENT
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#000000',
                    fontFamily: 'Georgia, serif',
                    fontStyle: 'italic'
                  }}>
                    "{item.original}"
                  </div>
                </div>

                <div>
                  <div style={{
                    fontSize: '11px',
                    color: '#6B7280',
                    textTransform: 'uppercase',
                    fontWeight: '600',
                    marginBottom: '6px',
                    letterSpacing: '0.05em'
                  }}>
                    SPECIFIC
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#000000',
                    fontWeight: '600'
                  }}>
                    "{item.fix}"
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Contact Form Footer */}
        <div style={{
          paddingTop: '48px',
          borderTop: '1px solid #000000'
        }}>
          {!submitted ? (
            <form onSubmit={handleContactSubmit}>
              <div style={{
                fontSize: '14px',
                fontWeight: '700',
                color: '#000000',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: '24px'
              }}>
                CONTACT ME HERE
              </div>

              <div style={{ marginBottom: '24px' }}>
                <div style={{
                  fontSize: '11px',
                  color: '#6B7280',
                  textTransform: 'uppercase',
                  fontWeight: '600',
                  letterSpacing: '0.05em',
                  marginBottom: '12px'
                }}>
                  ARE YOU FUNDRAISING RIGHT NOW? *
                </div>
                <div style={{ display: 'flex', gap: '16px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}>
                    <input
                      type="radio"
                      name="fundraising"
                      value="yes"
                      checked={fundraising === 'yes'}
                      onChange={(e) => setFundraising(e.target.value)}
                      style={{ marginRight: '8px' }}
                      required
                    />
                    YES
                  </label>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}>
                    <input
                      type="radio"
                      name="fundraising"
                      value="no"
                      checked={fundraising === 'no'}
                      onChange={(e) => setFundraising(e.target.value)}
                      style={{ marginRight: '8px' }}
                      required
                    />
                    NO
                  </label>
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '11px',
                  color: '#6B7280',
                  textTransform: 'uppercase',
                  fontWeight: '600',
                  letterSpacing: '0.05em',
                  marginBottom: '8px'
                }}>
                  YOUR BIGGEST FUNDRAISING CHALLENGE RIGHT NOW? (OPTIONAL)
                </label>
                <textarea
                  value={challenge}
                  onChange={(e) => setChallenge(e.target.value)}
                  placeholder="e.g., Finding lead investor, unclear unit economics, getting warm intros..."
                  style={{
                    width: '100%',
                    minHeight: '80px',
                    padding: '12px',
                    border: '1px solid #E5E7EB',
                    fontSize: '14px',
                    fontFamily: 'Inter, -apple-system, sans-serif',
                    outline: 'none',
                    resize: 'vertical'
                  }}
                  onFocus={(e) => e.target.style.border = '1px solid #000000'}
                  onBlur={(e) => e.target.style.border = '1px solid #E5E7EB'}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '11px',
                  color: '#6B7280',
                  textTransform: 'uppercase',
                  fontWeight: '600',
                  letterSpacing: '0.05em',
                  marginBottom: '8px'
                }}>
                  EMAIL ADDRESS *
                </label>
                <input
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="founder@startup.com"
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #E5E7EB',
                    fontSize: '14px',
                    fontFamily: 'Inter, -apple-system, sans-serif',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.border = '1px solid #000000'}
                  onBlur={(e) => e.target.style.border = '1px solid #E5E7EB'}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'start',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: '#374151'
                }}>
                  <input
                    type="checkbox"
                    checked={requestPromo}
                    onChange={(e) => setRequestPromo(e.target.checked)}
                    style={{ marginRight: '8px', marginTop: '2px' }}
                  />
                  Send me a promo code to analyze more decks
                </label>
              </div>

              <button
                type="submit"
                style={{
                  padding: '16px 32px',
                  background: '#000000',
                  border: 'none',
                  color: '#FFFFFF',
                  fontSize: '12px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}
              >
                SUBMIT
              </button>
            </form>
          ) : (
            <div style={{
              padding: '32px',
              background: '#F9FAFB',
              border: '1px solid #E5E7EB',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#000000',
                marginBottom: '8px'
              }}>
                SUBMITTED
              </div>
              <div style={{
                fontSize: '13px',
                color: '#6B7280'
              }}>
                We'll be in touch soon.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default MakeAlign;
